/**
 * First-class EEI API client.
 */

import {firestoreApiFetch} from './firestoreApiClient';

/**
 * @param {{q?:string, owner?:string, status?:string, keywords?:string|string[], createdFrom?:string, createdTo?:string, page?:number, size?:number}} params
 */
export async function listEeis(params = {}) {
    const sp = new URLSearchParams();
    if (params.q) sp.set('q', params.q);
    if (params.owner) sp.set('owner', params.owner);
    if (params.status) sp.set('status', params.status);
    if (params.keywords) {
        const kw = Array.isArray(params.keywords) ? params.keywords.join(',') : params.keywords;
        if (kw) sp.set('keywords', kw);
    }
    if (params.createdFrom) sp.set('createdFrom', params.createdFrom);
    if (params.createdTo) sp.set('createdTo', params.createdTo);
    if (params.page != null) sp.set('page', String(params.page));
    if (params.size != null) sp.set('size', String(params.size));
    const qs = sp.toString();
    return firestoreApiFetch(`/api/eeis${qs ? `?${qs}` : ''}`);
}

export async function getEei(idOrNumber) {
    return firestoreApiFetch(`/api/eeis/${encodeURIComponent(idOrNumber)}`);
}

/**
 * Per-EEI analytic coverage summary (question node from contract C1 plus
 * per-FINTEL contributions). Returns null on 404.
 * @param {string} idOrNumber EEI id or EEI number
 */
export async function getEeiAnalyticCoverage(idOrNumber) {
    return firestoreApiFetch(`/api/eeis/${encodeURIComponent(idOrNumber)}/analytic-coverage`);
}

export async function createEei(data) {
    const payload = {...data};
    if (Array.isArray(data?.targets)) {
        payload.targets = data.targets
            .map((t) => (typeof t === 'string' ? t : t?.entityRef))
            .filter(Boolean);
    }
    return firestoreApiFetch('/api/eeis', {method: 'POST', body: JSON.stringify(payload)});
}

export async function updateEei(idOrNumber, data) {
    const payload = {...data};
    if (Array.isArray(data?.targets)) {
        payload.targets = data.targets
            .map((t) => (typeof t === 'string' ? t : t?.entityRef))
            .filter(Boolean);
    }
    return firestoreApiFetch(`/api/eeis/${encodeURIComponent(idOrNumber)}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
}

/**
 * @deprecated Standalone gap→EEI removed. Use proposeIcrFromFintelGaps in icr.js.
 */
export async function createEeiFromFintelGap() {
    throw new Error(
        'Standalone EEI creation from gaps is disabled. Use Propose ICR from selected gaps.',
    );
}

export async function listReportGaps(reportId) {
    return firestoreApiFetch(`/api/reports/${encodeURIComponent(reportId)}/gaps`);
}

/** Enqueue similarity recompute for all gaps on a report (CM/admin). */
export async function recomputeReportGapSimilarity(reportId) {
    return firestoreApiFetch(`/api/reports/${encodeURIComponent(reportId)}/gaps/similarity/recompute`, {
        method: 'POST',
        body: '{}',
    });
}

/**
 * Link a persisted gap to an EEI or another Gap (CM/admin).
 * @param {string} reportId
 * @param {string} gapId
 * @param {{targetType: 'EEI'|'GAP', targetId: string}} body
 */
export async function linkReportGap(reportId, gapId, body) {
    return firestoreApiFetch(
        `/api/reports/${encodeURIComponent(reportId)}/gaps/${encodeURIComponent(gapId)}/links`,
        {method: 'POST', body: JSON.stringify(body)},
    );
}

export async function unlinkReportGap(reportId, gapId, linkId) {
    return firestoreApiFetch(
        `/api/reports/${encodeURIComponent(reportId)}/gaps/${encodeURIComponent(gapId)}/links/${encodeURIComponent(linkId)}`,
        {method: 'DELETE'},
    );
}

/** Search persisted gaps across FINTELs (CM/admin link picker / ICR form). */
export async function searchFintelGaps({
    q = '',
    excludeGapId,
    tag,
    status,
    limit = 20,
} = {}) {
    const sp = new URLSearchParams();
    if (q) sp.set('q', q);
    if (excludeGapId) sp.set('excludeGapId', excludeGapId);
    if (tag) sp.set('tag', tag);
    if (status) sp.set('status', status);
    if (limit != null) sp.set('limit', String(limit));
    const qs = sp.toString();
    return firestoreApiFetch(`/api/fintel-gaps${qs ? `?${qs}` : ''}`);
}

/** Re-export for callers that prefer the eei module. */
export {proposeIcrFromFintelGaps} from './icr';

export const EEI_STATUSES = ['Draft', 'Active', 'Closed'];
export const EEI_PRIORITIES = ['Low', 'Medium', 'High'];

/** Normalize legacy and EEI priority labels to Low / Medium / High. */
export function normalizeEeiPriority(priority) {
    if (priority == null || priority === '') return 'Medium';
    const p = String(priority).trim();
    if (/^high$/i.test(p)) return 'High';
    if (/^low$/i.test(p)) return 'Low';
    if (/^medium$/i.test(p)) return 'Medium';
    if (/^(critical|immediate)$/i.test(p)) return 'High';
    if (/^priority$/i.test(p)) return 'Medium';
    return 'Low';
}

export function eeiPriorityChipColor(priority) {
    const normalized = normalizeEeiPriority(priority);
    if (normalized === 'High') return 'error';
    if (normalized === 'Medium') return 'warning';
    return 'success';
}

/** Display label for EEI/ICR priority chips (e.g. "High Priority"). */
export function formatEeiPriorityLabel(priority) {
    return `${normalizeEeiPriority(priority)} Priority`;
}
